// Multi-select picker bound to the `deliverable.milestoneId` FK. Shows
// every live deliverable on the contract and lets the user tick which
// ones belong to this milestone. The picker is controlled by the parent
// form: ticking sets state; the parent's Save handler dispatches the
// bulk-set mutation alongside the milestone PUT.
//
// Tick-state rules:
//   - Already in THIS milestone     → ticked, enabled.
//   - Unattached                    → unticked, enabled.
//   - In a DIFFERENT milestone      → unticked, disabled, with a
//                                     "currently in <other milestone>"
//                                     hint so the user can see why they
//                                     can't grab it from here. (To move
//                                     it, the user goes to the other
//                                     milestone's picker, or to the
//                                     deliverable's own row.)
//
// Sorting: by dueDate ascending, undefined-due-date last, ties broken
// by name. Recurring deliverables share names; the due-date pin makes
// individual occurrences distinguishable at picker time.

import { useMemo } from "react";
import LoadingSpinner from "../../../common/LoadingSpinner";
import Alert from "../../../common/Alert";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import { useTrackingItems } from "../../../../hooks/useTracking";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import type { Deliverable, Milestone } from "../../../../types";

export interface MilestoneDeliverablesPickerProps {
  contractId: string;
  /** The milestone being edited. Ticking a deliverable means "set its
   *  milestoneId FK to this milestone's id". Required so the picker can
   *  derive ticked-vs-cross-milestone state from each deliverable's FK. */
  milestoneId: string;
  /** Other milestones on the same contract — used to look up the name
   *  shown in the "currently in <name>" hint for cross-milestone
   *  deliverables. Pass the contract's milestone list as-is; the picker
   *  picks the right one. */
  milestones: readonly Milestone[];
  /** The current ticked set, as deliverable IDs. The parent owns this
   *  state; the picker fires `onChange` to mutate it. */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export default function MilestoneDeliverablesPicker({
  contractId,
  milestoneId,
  milestones,
  value,
  onChange,
  disabled,
}: MilestoneDeliverablesPickerProps) {
  const { data, isPending, error } = useTrackingItems(
    contractId,
    "deliverable",
    { includeArchived: false, limit: 200 },
  );

  const milestoneNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of milestones) map.set(m.id, m.name);
    return map;
  }, [milestones]);

  if (isPending) {
    return (
      <div className="flex justify-center py-3">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        {error instanceof Error
          ? error.message
          : "Failed to load deliverables."}
      </Alert>
    );
  }

  const SORT_LAST = "9999-99-99";
  const deliverables = (data?.items ?? [])
    .filter((i): i is Deliverable => i.type === "deliverable")
    .slice()
    .sort((a, b) => {
      const ka = a.dueDate ?? SORT_LAST;
      const kb = b.dueDate ?? SORT_LAST;
      if (ka !== kb) return ka.localeCompare(kb);
      return a.name.localeCompare(b.name);
    });

  if (deliverables.length === 0) {
    return (
      <p
        className="text-caption text-slate-500 dark:text-slate-400"
        data-testid="milestone-deliverables-picker-empty"
      >
        No deliverables available on this contract. Add a deliverable first.
      </p>
    );
  }

  const toggle = (id: string) => {
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
    );
  };

  return (
    <div
      className="space-y-1 rounded-md border border-slate-200 p-2 max-h-56 overflow-y-auto dark:border-slate-700"
      data-testid="milestone-deliverables-picker"
      role="group"
      aria-label="Deliverables in this milestone"
    >
      {deliverables.map((d) => {
        const dueLabel = d.dueDate ? formatDayMonthYear(d.dueDate) : null;
        const isInOtherMilestone =
          !!d.milestoneId && d.milestoneId !== milestoneId;
        const otherMilestoneName = isInOtherMilestone
          ? (milestoneNameById.get(d.milestoneId!) ?? "another milestone")
          : null;
        const checkboxDisabled = disabled || isInOtherMilestone;
        // `checked` must mirror the controlled `value` set verbatim — using
        // `isInThisMilestone && value.includes(d.id)` would freeze the
        // visual tick state until the parent saves the FK, so unticking a
        // currently-in-milestone row would look like a no-op to the user.
        const isChecked = value.includes(d.id);
        const ariaLabelParts = [d.name];
        if (dueLabel) ariaLabelParts.push(`due ${dueLabel}`);
        if (otherMilestoneName)
          ariaLabelParts.push(`currently in ${otherMilestoneName}`);
        const ariaLabel = ariaLabelParts.join(", ");
        return (
          <label
            key={d.id}
            className={
              "flex items-start gap-2 py-1.5 text-caption " +
              (isInOtherMilestone
                ? "text-slate-500 dark:text-slate-400"
                : "text-slate-700 dark:text-slate-300")
            }
          >
            <input
              type="checkbox"
              className="mt-0.5 accent-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm"
              checked={isChecked}
              onChange={() => toggle(d.id)}
              disabled={checkboxDisabled}
              aria-label={ariaLabel}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="truncate">{d.name}</span>
                {dueLabel && (
                  <span
                    className="shrink-0 text-caption text-slate-500 dark:text-slate-400"
                    data-testid={`milestone-deliverables-picker-due-${d.id}`}
                  >
                    · due {dueLabel}
                  </span>
                )}
              </span>
              {otherMilestoneName && (
                <span
                  className="text-caption italic text-slate-500 dark:text-slate-400"
                  data-testid={`milestone-deliverables-picker-other-${d.id}`}
                >
                  currently in: {otherMilestoneName}
                </span>
              )}
            </span>
            <TrackingStatusBadge kind="deliverable" status={d.status} />
          </label>
        );
      })}
    </div>
  );
}
